import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDirection,
} from "context";
import useAuth from "hooks/useAuth";
import { getFromStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";
import { useAppDispatch } from "hooks/store";
import { setCredentials } from "reducers/authSlice";
import { useLazyGetProfileQuery } from "services/auth";
import LogoutModal from "components/LogoutModal";
import { useLazyGetLogoutQuery } from "services/auth";
import { removeFromStorage } from "helpers";
import { useDispatch } from "react-redux";
import { setToStorage } from "helpers";
import { useLanguageContext } from "context/Language";
import useTranslation from "hooks/Translation";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const currentLang = getFromStorage(STORAGE_KEYS.language);
  const t = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const [logoutMethod] = useLazyGetLogoutQuery();
  const { changeLanguage } = useLanguageContext();
  const usedispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);

  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [langMenu, setLangMenu] = useState(false);
  const [lang, setLang] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const currency = getFromStorage(STORAGE_KEYS.currency);

  const userData = useAuth();
  const [getUser] = useLazyGetProfileQuery();

  const getUserDetails = async () => {
    const token = getFromStorage(STORAGE_KEYS.token);
    try {
      const result = await getUser({}).unwrap();
      if (result?.status === 200 && token) {
        usedispatch(
          setCredentials({
            user: result?.data,
            token: token,
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLogout = async () => {
    try {
      const res = await logoutMethod({}).unwrap();

      navigate("/");
      removeFromStorage(STORAGE_KEYS.token);

      usedispatch(
        setCredentials({
          user: null,
          token: null,
        })
      );
    } catch (error) {
      // errorToast(error?.data?.message || "");
    }
  };

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenLangMenu = (event) => setLangMenu(event.currentTarget);
  const handleCloseLangMenu = () => setLangMenu(false);
  const handleOpenRtlMenu = (event) => setLang(event.currentTarget);
  const handleCloseRtlMenu = () => setLang(false);
  const handleOpenAccountMenu = (event) => setAccountMenu(event.currentTarget);
  const handleCloseAccountMenu = () => setAccountMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        icon={<Icon>email</Icon>}
        title={t.main.checkNewMessage}
      />
      {/* <NotificationItem
        icon={<Icon>podcasts</Icon>}
        title="Manage Podcast sessions"
      />
      <NotificationItem
        icon={<Icon>shopping_cart</Icon>}
        title="Payment successfully completed"
      /> */}
    </Menu>
  );

  const LanguageMenu = () => (
    <Menu
      anchorEl={langMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(langMenu)}
      onClose={handleCloseLangMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        selected={currency === "AED"}
        onClick={() => {
          setToStorage(STORAGE_KEYS.currency, "AED");
          window.location.reload();
        }}
        // icon={<Icon>email</Icon>}
        title="AED"
      />
      <NotificationItem
        selected={currency === "USD"}
        onClick={() => {
          setToStorage(STORAGE_KEYS.currency, "USD");
          window.location.reload();
        }}
        // icon={<Icon>podcasts</Icon>}
        title="USD"
      />
    </Menu>
  );

  const RtlMenu = () => (
    <Menu
      anchorEl={lang}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: currentLang === "ar" ? "right" : "left",
      }}
      open={Boolean(lang)}
      onClose={handleCloseRtlMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        selected={currentLang === "en"}
        onClick={() => {
          window.location.reload();
          setToStorage(STORAGE_KEYS.language, "en");
          changeLanguage("en");
        }}
        // icon={<Icon>email</Icon>}
        title="English"
      />
      <NotificationItem
        selected={currentLang === "ar"}
        onClick={() => {
          window.location.reload();
          setToStorage(STORAGE_KEYS.language, "ar");
          setDirection(dispatch, "rtl");
          changeLanguage("ar");
        }}
        // icon={<Icon>podcasts</Icon>}
        title="Arabic"
      />
      <NotificationItem
        selected={currentLang === "ru"}
        onClick={() => {
          window.location.reload();
          setToStorage(STORAGE_KEYS.language, "ru");
          changeLanguage("ru");
        }}
        // icon={<Icon>podcasts</Icon>}
        title="Russian"
      />
    </Menu>
  );

  const AccountMenu = () => {
    return (
      <Menu
        anchorEl={accountMenu}
        anchorReference={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(accountMenu)}
        onClose={handleCloseAccountMenu}
        sx={{ mt: 2 }}
      >
        <NotificationItem
          onClick={() => navigate("/my-account")}
          Link="/my-account"
          icon={<Icon>account_circle</Icon>}
          title={t.main.myProfile}
        />
        <NotificationItem
          onClick={() => setOpenModal(true)}
          icon={<Icon>logout</Icon>}
          title={t.main.logout}
        />
      </Menu>
    );
  };

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  useEffect(() => {
    const token = getFromStorage(STORAGE_KEYS.token);
    if (token) {
      usedispatch(
        setCredentials({
          user: null,
          token: token,
        })
      );
    }
    getUserDetails();
  }, []);

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={light}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox> */}
            <MDBox color={light ? "white" : "inherit"}>
              {/* <Link to="/my-account"> */}

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenRtlMenu}
              >
                <p style={{ fontSize: "14px", fontWeight: "bold" }}>
                  {currentLang === "en"
                    ? "En"
                    : currentLang === "ru"
                    ? "Ru"
                    : "Ar"}
                </p>
                <Icon sx={iconsStyle}>arrow_drop_down</Icon>
              </IconButton>
              {RtlMenu()}

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenLangMenu}
              >
                <p style={{ fontSize: "14px", fontWeight: "bold" }}>
                  {currency === "USD" ? "USD" : "AED"}
                </p>
                <Icon sx={iconsStyle}>arrow_drop_down</Icon>
              </IconButton>
              {LanguageMenu()}

              <IconButton
                onClick={handleOpenAccountMenu}
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              {AccountMenu()}
              {/* </Link> */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
      <LogoutModal
        open={openModal}
        setOpen={setOpenModal}
        handleDelete={() => handleLogout()}
      />
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
